<template>
  <div>
    <panel>
      <div class="row form-group">
        <div class="col-12">
          <yes-or-no class="form-group" :model="schema.data.deMinimis.isDeMinimis" />
        </div>
      </div>

      <got-minimis v-if="schema.data.$model.deMinimis.isDeMinimis !== false" :model="schema.data.deMinimis" />
    </panel>

    <panel
      title="Formularz informacji przedstawianych przy ubieganiu się o pomoc de minimis"
      tooltip="Stosuje się do pomocy de minimis udzielanej na warunkach określonych w rozporządzeniu Komisji (UE) nr 2023/2831 z dnia 13 grudnia 2023 r. w sprawie stosowania art. 107 i 108 Traktatu o funkcjonowaniu Unii Europejskiej do pomocy de minimis (Dz. Urz. UE L 2023/2831 z 15.12.2023 r.)"
    >
      <w-radio-list
        class="form-group"
        :model="subjectData.isCompanyPartner"
        :values="isCompanyPartnerOptions"
        tooltip="2) Wypełnia się w przypadku, gdy o pomoc de minimis wnioskuje wspólnik spółki cywilnej, jawnej albo
            partnerskiej albo komplementariusz spółki komandytowej albo komandytowo-akcyjnej niebędący akcjonariuszem
            lub inny podmiot na którego została przeniesiona odpowiedzialność podatkowa, w związku z działalnością
            prowadzoną w tej spółce (podaje się informacje dotyczące tego wspólnika, komplementariusza lub osoby
            trzeciej na którą przeniesiono odpowiedzialność podatkową). Wypełnia się również, gdy o pomoc wnioskuje
            wspólnik jednoosobowej spółki z ograniczoną odpowiedzialnością albo akcjonariusz prostej spółki akcyjnej, w
            związku z działalnością prowadzoną w tej spółce."
      />

      <template v-if="subjectData.$model.isCompanyPartner">
        <label class="label">
          A1. Informacje dotyczące wnioskodawcy niebędącego podmiotem, któremu ma być udzielona pomoc de minimis
        </label>

        <person :model="subjectData.companyPartner">
          <div class="row form-group">
            <div class="col-6">
              <w-input
                tooltip="3) O ile posiada identyfikator podatkowy NIP."
                :model="subjectData.companyPartner.identyfierNip"
              />
            </div>

            <div class="col-6">
              <w-input :model="subjectData.companyPartner.companyName" />
            </div>
          </div>
        </person>
      </template>

      <div class="row form-group">
        <div class="col-12 subject-type">
          <w-radio-list
            tooltip="5) Zaznacza się właściwą pozycję znakiem X."
            :model="subjectData.subjectType"
            :values="getDeMinimisApplicant"
          />
        </div>
      </div>

      <div v-if="schema.data.$model.subjectData.subjectType === 6" class="row form-group">
        <div class="col-12">
          <w-textarea :model="subjectData.otherSubject" />
        </div>
      </div>

      <div class="row form-group">
        <div class="col-12">
          <w-radio-list
            tooltip="5) Zaznacza się właściwą pozycję znakiem X."
            :model="subjectData.companyType"
            :values="companyType"
          />
        </div>
      </div>

      <div class="row form-group">
        <label class="col-12 label-width">
          <template>{{ subjectData.activityClass.$title }}*</template>

          <w-tooltip>
            6) Podaje się klasę działalności, w związku z którą podmiot ubiega się o pomoc de minimis. Jeżeli brak jest
            możliwości ustalenia jednej takiej działalności, podaje się klasę PKD tej działalności, która generuje
            największy przychód.
          </w-tooltip>
        </label>

        <w-input
          v-for="(item, index) in subjectData.activityClassDigits.$items"
          :key="`index_${index}`"
          class="col-1"
          mask="X"
          title=""
          :model="item.$item.digit"
          :show-error="false"
        />

        <w-error class="col-12" :model="subjectData.activityClass" />
      </div>

      <div class="row form-group">
        <div class="col-4">
          <w-datepicker position="bottom-left" :model="subjectData.createDate" />
        </div>
      </div>

      <label class="label form-group">
        Powiązania z innymi przedsiębiorcami
        <w-tooltip>
          7) Za powiązane nie uważa się podmiotów, w przypadku których powiązanie występuje wyłącznie za pośrednictwem
          organu publicznego, np. Skarbu Państwa, jednostki samorządu terytorialnego.
        </w-tooltip>
      </label>

      <radio-render
        :model="subjectData.relationships"
        :model-items="[
          { model: subjectData.relationships.speakLaw },
          { model: subjectData.relationships.appointLaw },
          { model: subjectData.relationships.dominantInfluent },
          { model: subjectData.relationships.shareholder },
          { model: subjectData.relationships.ratio }
        ]"
      />

      <template v-if="isTrue(subjectData.$model.relationships)">
        <label class="label">{{ subjectData.relationshipsDetails.$title }}</label>

        <w-form-table>
          <thead>
            <tr>
              <th class="middle" colspan="3">
                {{ subjectData.relationshipsDetails.identyfierNip.$title }}
              </th>
            </tr>

            <tr>
              <th class="middle" width="5%">
                Lp.
              </th>

              <th class="middle">
                NIP
              </th>

              <th class="middle" width="8%" />
            </tr>
          </thead>

          <tbody>
            <nip-row
              v-for="(item, index) in subjectData.relationshipsDetails.identyfierNip.$items"
              :key="index"
              :item="item.$item"
              :index="index"
            />
          </tbody>

          <tfoot>
            <tr>
              <td colspan="3">
                <w-add-row :model="subjectData.relationshipsDetails.identyfierNip" />
              </td>
            </tr>
          </tfoot>
        </w-form-table>

        <w-input
          :mask="moneyMask"
          :model="subjectData.relationshipsDetails.totalAmountString"
          tooltip="8) Podaje się wartość pomocy w euro obliczoną zgodnie z art. 11 ust. 3 ustawy z dnia 30 kwietnia 2004 r. o postępowaniu w sprawach dotyczących pomocy publicznej (Dz. U. z 2023 r. poz. 702), rozporządzeniem Rady Ministrów wydanym na podstawie art. 11 ust. 2 tej ustawy oraz właściwymi przepisami unijnymi."
        />
      </template>

      <label class="label">
        Informacja o utworzeniu wnioskodawcy w wyniku podziału innego przedsiębiorcy lub połączenia z innym
        przedsiębiorcą, w tym przez przejęcie innego przedsiębiorcy, lub przekształcenia przedsiębiorcy
      </label>

      <radio-render
        :model="subjectData.create"
        :model-items="[
          { model: subjectData.create.conectedCompany },
          { model: subjectData.create.acquisitionCompany },
          { model: subjectData.create.divisionCompany },
          { model: subjectData.create.transformationCompany }
        ]"
      />

      <template v-if="!subjectData.createDetails.$disabled">
        <label class="label">{{ subjectData.createDetails.$title }}</label>

        <w-form-table>
          <thead>
            <tr>
              <th class="middle" colspan="3">
                {{ subjectData.createDetails.identyfierNip.$title }}
              </th>
            </tr>

            <tr>
              <th class="middle" width="5%">
                Lp.
              </th>

              <th class="middle">
                NIP
              </th>

              <th class="middle" width="8%" />
            </tr>
          </thead>

          <tbody>
            <nip-row
              v-for="(item, index) in subjectData.createDetails.identyfierNip.$items"
              :key="index"
              :item="item.$item"
              :index="index"
            />
          </tbody>

          <tfoot>
            <tr>
              <td colspan="3">
                <w-add-row :model="subjectData.createDetails.identyfierNip" />
              </td>
            </tr>
          </tfoot>
        </w-form-table>

        <input-row
          :model-items="[
            {
              model: subjectData.createDetails.totalAmountString,
              mask: moneyMask,
              tooltip:
                '8) Podaje się wartość pomocy w euro obliczoną zgodnie z art. 11 ust. 3 ustawy z dnia 30 kwietnia 2004 r. o postępowaniu w sprawach dotyczących pomocy publicznej, rozporządzeniem Rady Ministrów wydanym na podstawie art. 11 ust. 2  tej ustawy oraz właściwymi przepisami unijnymi.'
            }
          ]"
        />
      </template>

      <template v-if="!subjectData.divisionDetails.$disabled && !subjectData.divisionBeforeDivision.$disabled">
        <input-row
          :title="subjectData.divisionDetails.$title"
          :model-items="[
            {
              model: subjectData.divisionDetails.identyfierNip
            },
            {
              model: subjectData.divisionDetails.totalAmountString,
              mask: moneyMask,
              tooltip:
                '8) Podaje się wartość pomocy w euro obliczoną zgodnie z art. 11 ust. 3 ustawy z dnia 30 kwietnia 2004 r. o postępowaniu w sprawach dotyczących pomocy publicznej, rozporządzeniem Rady Ministrów wydanym na podstawie art. 11 ust. 2  tej ustawy oraz właściwymi przepisami unijnymi.'
            }
          ]"
        />

        <input-row
          :title="subjectData.divisionBeforeDivision.$title"
          :model-items="[
            {
              model: subjectData.divisionBeforeDivision.totalAmount,
              tooltip:
                '8) Podaje się wartość pomocy w euro obliczoną zgodnie z art. 11 ust. 3 ustawy z dnia 30 kwietnia 2004 r. o postępowaniu w sprawach dotyczących pomocy publicznej, rozporządzeniem Rady Ministrów wydanym na podstawie art. 11 ust. 2  tej ustawy oraz właściwymi przepisami unijnymi.'
            },
            { model: subjectData.divisionBeforeDivision.entrepreneurAmount },
            { model: subjectData.divisionBeforeDivision.subjectAmount }
          ]"
        />
      </template>
    </panel>

    <panel
      title="B. Informacje dotyczące sytuacji ekonomicznej podmiotu któremu ma być udzielona pomoc de minimis"
      tooltip="9) Wypełnia się jedynie w przypadku podmiotów, którym ma być udzielona pomoc de minimis, do obliczenia wartości której konieczne jest ustalenie ich stopy referencyjnej (tj. w formie takiej jak:pożyczki, gwarancje, odroczenia, rozłożenia na raty), z wyjątkiem podmiotów, którym pomoc de minimis ma być udzielona na podstawie art. 34a ustawy z dnia 8 maja 1997 r. o poręczeniach i gwarancjach udzielanych przez Skarb Państwa oraz niektóre osoby prawne (Dz.U. 2024 poz. 291) oraz będących osobami fizycznymi, którzy na dzień złożenia informacji określonych w niniejszym rozporządzeniu nie rozpoczęli prowadzenia działalności gospodarczej."
    >
      <div class="row">
        <w-alert clas="col-12" alert-type="info">Część B. formularza nie jest wymagalna</w-alert>
      </div>

      <radio-render :model-items="[{ model: subjectData.economic.bankruptProcces }]" :is-required="false" />

      <div class="row form-group">
        <div class="col-12">
          <yes-no-not
            tooltip="10) Ocena kredytowa B- oznacza wysokie ryzyko kredytowe. Zdolność do obsługi zobowiązań istnieje jedynie przy sprzyjających warunkach zewnętrznych. Poziom odzyskania wierzytelności w przypadku wystąpienia niewypłacalności jest średni lub niski."
            :model="subjectData.economic.creditRating"
          />
        </div>
      </div>

      <radio-render
        :model="subjectData.economic.economicDetails"
        :model-items="[
          { model: subjectData.economic.economicDetails.increasingLoses },
          { model: subjectData.economic.economicDetails.entityDecreases },
          { model: subjectData.economic.economicDetails.increaseInStock },
          {
            model: subjectData.economic.economicDetails.excessProduction,
            tooltip: '11) Dotyczy wyłącznie producentów.'
          },
          { model: subjectData.economic.economicDetails.flowReduction },
          { model: subjectData.economic.economicDetails.increaseInDebt },
          { model: subjectData.economic.economicDetails.increaseInterest },
          { model: subjectData.economic.economicDetails.netAssets },
          { model: subjectData.economic.economicDetails.other }
        ]"
      />

      <template v-if="isTrue(subjectData.$model.economic.economicDetails)">
        <div class="row form-group">
          <div class="col-12">
            <w-textarea :model="subjectData.economic.economicDetails.otherCircumstances" />
          </div>
        </div>
      </template>
    </panel>

    <panel
      title="C. Informacje dotyczące działalności gospodarczej prowadzonej przez podmiot któremu ma być udzielona pomoc de minimis"
    >
      <radio-render
        :model="subjectData.activity"
        :model-items="[
          {
            model: subjectData.activity.fishingSector,
            tooltip:
              '12) Objętych rozporządzeniem Parlamentu Europejskiego i Rady (UE) nr 1379/2013 z dnia 11 grudnia 2013 r. w sprawie wspólnej organizacji rynków produktów rybołówstwa i akwakultury, zmieniającym rozporządzenia Rady (WE) nr 1184/2006 i (WE) nr 1224/2009 oraz uchylającym rozporządzenie Rady (WE) nr 104/2000 (Dz. Urz. UE L 354 z 28.12.2013, str. 1, z późn. zm.).'
          },
          { model: subjectData.activity.agriculturalProduction },
          { model: subjectData.activity.agriculturalRotation },
          {
            model: subjectData.activity.roadTransport,
            tooltip:
              '12) Objętych rozporządzeniem Parlamentu Europejskiego i Rady (UE) nr 1379/2013 z dnia 11 grudnia 2013 r. w sprawie wspólnej organizacji rynków produktów rybołówstwa i akwakultury, zmieniającym rozporządzenia Rady (WE) nr 1184/2006 i (WE) nr 1224/2009 oraz uchylającym rozporządzenie Rady (WE) nr 104/2000 (Dz. Urz. UE L 354 z 28.12.2013, str. 1, z późn. zm.).'
          }
        ]"
      />

      <radio-render
        v-if="subjectData.$model.activity.roadTransport"
        :model-items="[{ model: subjectData.activity.buyingVehicles }]"
        :is-required="false"
      />

      <radio-render :model-items="[{ model: subjectData.activity.selectedActivity }]" :is-required="false" />

      <div class="row form-group">
        <div class="col-12">
          <yes-no-not
            tooltip="13) Rozdzielność rachunkowa określonej działalności gospodarczej polega na prowadzeniu odrębnej ewidencji dla tej działalności gospodarczej oraz prawidłowym przypisywaniu przychodów i kosztów na podstawie konsekwentnie stosowanych i mających obiektywne uzasadnienie metod, a także określeniu w dokumentacji, o której mowa w art. 10 ustawy z dnia 29 września 1994 r. o rachunkowości (Dz.U. z 2023 r. poz. 120, z późn. zm.), zasad prowadzenia odrębnej ewidencji oraz metod przypisywania kosztów i przychodów."
            :model="subjectData.activity.accountingSeparation"
          />
        </div>
      </div>

      <div
        v-if="
          (subjectData.$model.activity.fishingSector ||
            subjectData.$model.activity.agriculturalProduction ||
            subjectData.$model.activity.roadTransport) &&
            subjectData.$model.activity.accountingSeparation === 1
        "
        class="row form-group"
      >
        <div class="col-12">
          <w-textarea :model="subjectData.activity.separationDetails" :title="false" />
        </div>
      </div>
    </panel>

    <panel
      title="D. Informacje dotyczące pomocy otrzymanej w odniesieniu do tych samych kosztów, na pokrycie których ma być przeznaczona wnioskowana pomoc de minimis"
    >
      <radio-render :model-items="[{ model: subjectData.deMinimisEstimate.identifiedCosts }]" :is-required="false" />

      <radio-render
        v-if="subjectData.$model.deMinimisEstimate.identifiedCosts"
        :model-items="[{ model: subjectData.deMinimisEstimate.otherHelp }]"
        :is-required="false"
      />

      <template v-if="subjectData.$model.deMinimisEstimate.otherHelp">
        <div class="row form-group">
          <div class="col-12">
            <instruction-minimis />

            <br />

            <label class="label">
              Jeśli tak, należy wypełnić poniższą tabelę
              <w-tooltip>14) Wypełnia się zgodnie z Instrukcją wypełnienia tabeli w części D formularza.</w-tooltip>w
              odniesieniu do ww. pomocy innej niż de minimis oraz pomocy de minimis na te same koszty.
            </label>

            <w-form-table>
              <thead>
                <tr>
                  <th class="middle" rowspan="2">
                    Lp.
                  </th>

                  <th class="middle" rowspan="2">
                    Dzień udzielenia pomocy
                  </th>

                  <th class="middle" rowspan="2">
                    Podmiot udzielający pomocy
                  </th>

                  <th class="middle" colspan="2">
                    Podstawa prawna udzielenia pomocy
                  </th>

                  <th class="middle" rowspan="2">
                    Forma pomocy&nbsp;
                    <w-tooltip tooltip="Należy podać wyłącznie kod oznaczający właściwą formę pomocy " />
                  </th>

                  <th class="middle" colspan="2">
                    Wartość otrzymanej pomocy
                  </th>

                  <th class="middle" rowspan="2">
                    Przeznaczenie pomocy&nbsp;
                    <w-tooltip
                      tooltip="Należy podać kod wskazujący przeznaczenie otrzymanej pomocy według tabeli zamieszczonej w instrukcji. "
                    />
                  </th>

                  <th rowspan="2" />
                </tr>

                <tr>
                  <th class="middle">
                    informacje podstawowe
                  </th>

                  <th class="middle">
                    informacje szczegółowe
                  </th>

                  <th class="middle">
                    nominalna
                  </th>

                  <th class="middle">
                    brutto
                  </th>
                </tr>

                <tr>
                  <th />
                  <th class="middle">
                    1
                  </th>

                  <th class="middle">
                    2
                  </th>

                  <th class="middle">
                    3a
                  </th>

                  <th class="middle">
                    3b
                  </th>

                  <th class="middle">
                    4
                  </th>

                  <th class="middle">
                    5a
                  </th>

                  <th class="middle">
                    5b
                  </th>

                  <th class="middle">
                    6
                  </th>
                  <th />
                </tr>
              </thead>

              <tbody>
                <estimate-row
                  v-for="(item, index) in subjectData.deMinimisEstimate.estimate.$items"
                  :key="index + '.Estimate'"
                  :item="item.$item"
                  :index="index"
                />
              </tbody>

              <tfoot>
                <tr>
                  <td colspan="10" class="text-right">
                    <w-add-row :model="subjectData.deMinimisEstimate.estimate" />
                  </td>
                </tr>
              </tfoot>
            </w-form-table>
          </div>
        </div>

        <label class="label">{{ subjectData.deMinimisEstimate.additionalHelp.$title }}</label>

        <textarea-row
          :model-items="[
            { model: subjectData.deMinimisEstimate.additionalHelp.descriptionUndertaking },
            { model: subjectData.deMinimisEstimate.additionalHelp.eligibleCosts }
          ]"
        />

        <input-row
          :model-items="[
            { model: subjectData.deMinimisEstimate.additionalHelp.aidIntensity },
            { model: subjectData.deMinimisEstimate.additionalHelp.helpGiven }
          ]"
        />

        <textarea-row
          :model-items="[
            { model: subjectData.deMinimisEstimate.additionalHelp.projectLocation },
            { model: subjectData.deMinimisEstimate.additionalHelp.target },
            { model: subjectData.deMinimisEstimate.additionalHelp.implementationStages }
          ]"
        />

        <div class="row form-group">
          <div class="col-6">
            <w-datepicker :model="subjectData.deMinimisEstimate.additionalHelp.startDate" />
          </div>

          <div class="col-6">
            <w-datepicker :model="subjectData.deMinimisEstimate.additionalHelp.endDate" />
          </div>
        </div>
      </template>
    </panel>

    <panel title="E. Informacje dotyczące osoby upoważnionej do przedstawienia informacji">
      <person :model="subjectData.authorizedPerson" :has-address="false">
        <div class="row form-group">
          <div class="col-6">
            <w-input :model="subjectData.authorizedPerson.phoneNumber" />
          </div>

          <div class="col-6">
            <w-input :model="subjectData.authorizedPerson.jobPosition" />
          </div>
        </div>
      </person>
    </panel>
  </div>
</template>

<script>
import components from '@witkac/bootstrap4-components';

import Panel from '@/components/panel/Panel.vue';
import { ow004Mixin } from '@/mixins/document/ow004';
import { moneyMask } from '@/utils/moneyMask';

import EstimateRow from '../deMinimis/estimateRow';
import GotMinimis from '../deMinimis/GotMinimis';
import NipRow from '../deMinimis/NipRow';
import InputRow from '../InputRow';
import Person from '../Person';
import RadioRender from '../RadioRender';
import InstructionMinimis from '../staticComponents/InstructionMinimis';
import TextareaRow from '../TextareaRow';
import YesNoNot from '../YesNoNotRadio';
import YesOrNo from '../YesOrNoRadio';

// @vuese
// DeMinimis component
// @Group Deminimis
export default {
  components: {
    EstimateRow,
    GotMinimis,
    InputRow,
    InstructionMinimis,
    NipRow,
    RadioRender,
    Panel,
    Person,
    TextareaRow,
    YesNoNot,
    YesOrNo,
    ...components
  },
  mixins: [ow004Mixin],
  props: {
    // Wartości obiektu deMinimis, które będą uzupełniane
    schema: { type: Object, required: true }
  },
  data() {
    const isCompanyPartnerOptions = [
      { text: 'Dotyczy', value: true },
      { text: 'Nie dotyczy', value: false }
    ];
    return {
      isCompanyPartnerOptions,
      moneyMask
    };
  },
  computed: {
    subjectData() {
      return this.schema.data.subjectData;
    }
  }
};
</script>

<style>
.subject-type .radio-list .radio {
  display: block;
}
</style>
